import React, { useState, useEffect, useCallback } from 'react';
import { toUrl } from '../lib/config';

interface OlayRow {
  id: number;
  olayTuru: string;
  olayinKonusu: string;
  digerBelirtiniz?: string;
  gizlilikTalebi: string;
  olayAnlatiniz: string;
  olayaIliskinVarsaGorus?: string;
  hukukiYansimasi: string;
  createdAt: string;
}

const IstenmeyenOlayYonetim: React.FC = () => {
  const [olaylar, setOlaylar] = useState<OlayRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOlay, setSelectedOlay] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const fetchOlaylar = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const url = toUrl('/api/istenmeyen-olay');
      const res = await fetch(url);
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Error response:', errorText);
        throw new Error(`İstenmeyen olaylar yüklenemedi: ${res.status}`);
      }
      const data = await res.json();
      setOlaylar(data.data || []);
    } catch (e: any) {
      console.error('Fetch error:', e);
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOlaylar();
  }, [fetchOlaylar]);

  const openDeleteModal = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    
    try {
      setLoading(true);
      setError('');
      const res = await fetch(toUrl(`/api/istenmeyen-olay/${deleteId}`), {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Silme işlemi başarısız');
      closeDeleteModal();
      await fetchOlaylar();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleShowDetail = async (id: number) => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(toUrl(`/api/istenmeyen-olay/${id}`));
      if (!res.ok) throw new Error('Olay detayı yüklenemedi');
      const data = await res.json();
      setSelectedOlay(data.data);
      setShowDetailModal(true);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredOlaylar = olaylar.filter(olay =>
    olay.olayTuru?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    olay.olayinKonusu?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    olay.olayAnlatiniz?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOlayTuruBadge = (tur: string) => {
    const colors = {
      'Gerçekleşti': { bg: '#fee2e2', color: '#991b1b' },
      'Ramak Kaldı': { bg: '#fef3c7', color: '#92400e' }
    };
    const style = colors[tur as keyof typeof colors] || { bg: '#f3f4f6', color: '#374151' };
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 500,
        backgroundColor: style.bg,
        color: style.color
      }}>
        {tur}
      </span>
    );
  };

  return (
    <div className="admin-page" style={{ padding: 20 }}>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>İstenmeyen Olay Yönetimi</h2>
        <div style={{ fontSize: 14, color: '#6b7280' }}>
          Toplam: {olaylar.length} olay bildirimi
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: 6 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <input
          type="text"
          placeholder="Olay türü, konu veya olay anlatımına göre ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 15px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 14
          }}
        />
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 20 }}>Yükleniyor...</div>}

      {!loading && filteredOlaylar.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          Henüz olay bildirimi bulunmuyor.
        </div>
      )}

      {!loading && filteredOlaylar.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>ID</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Olay Türü</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Konu</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Gizlilik</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Hukuki Yansıma</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Tarih</th>
                <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, fontSize: 13 }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredOlaylar.map((olay, idx) => (
                <tr key={olay.id} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                  <td style={{ padding: 12, fontSize: 13 }}>{olay.id}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{getOlayTuruBadge(olay.olayTuru)}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>
                    {olay.olayinKonusu}
                    {olay.digerBelirtiniz && <span style={{ color: '#6b7280', fontSize: 12 }}> ({olay.digerBelirtiniz})</span>}
                  </td>
                  <td style={{ padding: 12, fontSize: 13 }}>{olay.gizlilikTalebi}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{olay.hukukiYansimasi}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{formatDate(olay.createdAt)}</td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    <button
                      onClick={() => handleShowDetail(olay.id)}
                      style={{
                        padding: '6px 12px',
                        marginRight: 8,
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Detay
                    </button>
                    <button
                      onClick={() => openDeleteModal(olay.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Silme Onay Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeDeleteModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              padding: 24,
              maxWidth: 400,
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', color: '#991b1b' }}>Olay Bildirimini Sil</h3>
            <p style={{ margin: '0 0 24px 0', color: '#374151' }}>
              Bu olay bildirimini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={closeDeleteModal}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                İptal
              </button>
              <button
                onClick={confirmDelete}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detay Modal */}
      {showDetailModal && selectedOlay && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20
          }}
          onClick={() => setShowDetailModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              maxWidth: 800,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: 24
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>İstenmeyen Olay Detayı - ID: {selectedOlay.id}</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
              >
                Kapat
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <DetailItem label="Olay Türü" value={getOlayTuruBadge(selectedOlay.olayTuru)} />
              <DetailItem label="Olayın Konusu" value={selectedOlay.olayinKonusu} />
              {selectedOlay.digerBelirtiniz && (
                <DetailItem label="Diğer (Açıklama)" value={selectedOlay.digerBelirtiniz} />
              )}
              <DetailItem label="Gizlilik Talebi" value={selectedOlay.gizlilikTalebi} />
              <DetailItem label="Hukuki Yansıması" value={selectedOlay.hukukiYansimasi} />
              <DetailItem label="Kayıt Tarihi" value={formatDate(selectedOlay.createdAt)} />
            </div>

            <div style={{ paddingTop: 20, borderTop: '1px solid #e5e7eb', marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12 }}>Olay Anlatımı</h4>
              <div style={{ 
                padding: 16, 
                backgroundColor: '#f9fafb', 
                borderRadius: 6, 
                fontSize: 14,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap'
              }}>
                {selectedOlay.olayAnlatiniz}
              </div>
            </div>

            {selectedOlay.olayaIliskinVarsaGorus && (
              <div style={{ paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
                <h4 style={{ marginBottom: 12 }}>Olaya İlişkin Görüş ve Öneriler</h4>
                <div style={{ 
                  padding: 16, 
                  backgroundColor: '#f0fdf4', 
                  borderRadius: 6, 
                  fontSize: 14,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedOlay.olayaIliskinVarsaGorus}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DetailItem: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 500 }}>{value || '-'}</div>
  </div>
);

export default IstenmeyenOlayYonetim;
